import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Package, Barcode, Truck, SealCheck, CaretRight, ArrowLeft, PaperPlaneRight, Warning, Clock, CircleNotch,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { MaterialIcon } from "../../lib/materialIcons";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { SerialStep } from "./SerialStep";
import { PhotoPicker } from "./PhotoPicker";
import { VerifyStep } from "./VerifyStep";

export const fmt = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(2).replace(/\.00$/, ""));
const pad = (n) => String(n).padStart(2, "0");
const nowParts = () => { const d = new Date(); return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` }; };

const STEPS = [
  { key: "items", label: "Materials", icon: Package },
  { key: "serials", label: "Serial No.", icon: Barcode },
  { key: "transport", label: "Vehicle & Photos", icon: Truck },
  { key: "verify", label: "Verify", icon: SealCheck },
];

const Stepper = ({ step }) => (
  <div className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-3 bg-slate-50 border-b border-slate-200 overflow-x-auto" data-testid="wizard-stepper">
    {STEPS.map((s, i) => {
      const idx = STEPS.findIndex((x) => x.key === step);
      const state = i < idx ? "done" : i === idx ? "active" : "todo";
      return (
        <div key={s.key} className="flex items-center gap-1 sm:gap-2 shrink-0">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${state === "active" ? "bg-brand-500 text-slate-950 ring-4 ring-brand-500/20" : state === "done" ? "bg-slate-900 text-white" : "bg-white ring-1 ring-slate-300 text-slate-400"}`}>
            <s.icon size={14} weight="bold" />
          </span>
          <span className={`text-[11px] font-semibold ${state === "active" ? "text-slate-900" : "text-slate-400"} ${state === "active" ? "" : "hidden sm:inline"}`}>{s.label}</span>
          {i < STEPS.length - 1 && <span className={`w-4 sm:w-8 h-px ${i < idx ? "bg-slate-900" : "bg-slate-300"}`} />}
        </div>
      );
    })}
  </div>
);

const PickList = ({ lines, checked, toggle, qty, setQty }) => (
  <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 bg-white overflow-hidden" data-testid="pick-list">
    {lines.map((l) => {
      const on = checked.has(l.key);
      return (
        <div key={l.key} onClick={(e) => e.target.tagName !== "INPUT" && e.target.getAttribute("role") !== "checkbox" && !e.target.closest("button") && toggle(l.key)}
          className={`flex items-center gap-3 px-3 sm:px-4 py-3 cursor-pointer transition-colors ${on ? "bg-brand-50/60" : "hover:bg-slate-50"}`}>
          <Checkbox data-testid={`pick-check-${l.key}`} checked={on} onCheckedChange={() => toggle(l.key)} />
          <MaterialIcon iconKey={l.icon_key} size={40} />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm leading-tight">{l.label}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Pending <b className="font-mono text-slate-700">{fmt(l.pending)}</b> {l.unit}{l.serial && <span className="ml-2 text-sky-600 font-semibold">· Serial required</span>}</div>
          </div>
          <input data-testid={`pick-qty-${l.key}`} type="number" min="0" max={l.pending} inputMode="numeric" value={qty[l.key] ?? ""} disabled={!on}
            onChange={(e) => setQty((q) => ({ ...q, [l.key]: e.target.value }))}
            className="w-16 h-10 text-center text-sm font-mono font-bold rounded-lg border border-slate-200 bg-white focus:ring-2 focus:ring-brand-400 focus:outline-none disabled:opacity-40" />
        </div>
      );
    })}
  </div>
);

const Transport = ({ form, setForm, photos, setPhotos }) => {
  const inp = "w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-400";
  const lab = "text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><span className={lab}>Vehicle Number *</span><input data-testid="dispatch-vehicle" className={`${inp} font-mono uppercase tracking-wider`} value={form.vehicle_no} placeholder="UP 70 AB 1234" onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} /></div>
        <div><span className={lab}>Driver Name *</span><input data-testid="dispatch-driver" className={inp} value={form.driver_name} placeholder="Driver full name" onChange={(e) => setForm({ ...form, driver_name: e.target.value })} /></div>
        <div><span className={lab}>Dispatch Date</span><input data-testid="dispatch-date" type="date" className={inp} value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} /></div>
        <div><span className={lab}>Exact Time</span>
          <div className="flex gap-2">
            <input data-testid="dispatch-time" type="time" className={inp} value={form.dispatch_time} onChange={(e) => setForm({ ...form, dispatch_time: e.target.value })} />
            <button type="button" data-testid="dispatch-time-now" onClick={() => { const n = nowParts(); setForm({ ...form, dispatch_date: n.date, dispatch_time: n.time }); }}
              className="h-11 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold flex items-center gap-1 shrink-0 hover:bg-slate-800"><Clock size={14} /> Now</button>
          </div>
        </div>
        <div className="sm:col-span-2"><span className={lab}>Note (optional)</span><input data-testid="dispatch-note" className={inp} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Anything the site team should know…" /></div>
      </div>
      <PhotoPicker photos={photos} setPhotos={setPhotos} />
    </div>
  );
};

export function DispatchWizard({ x, onDone, onCancel }) {
  const pending = useMemo(() => (x.dispatch_lines || []).filter((l) => l.pending > 0), [x]);
  const [step, setStep] = useState("items");
  const [checked, setChecked] = useState(() => new Set(pending.map((l) => l.key)));
  const [qty, setQty] = useState(() => Object.fromEntries(pending.map((l) => [l.key, String(fmt(l.pending))])));
  const [serials, setSerials] = useState({});
  const [form, setForm] = useState(() => { const n = nowParts(); return { vehicle_no: "", driver_name: "", dispatch_date: n.date, dispatch_time: n.time, note: "" }; });
  const [photos, setPhotos] = useState([]);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState("");

  const selected = pending.filter((l) => checked.has(l.key) && Number(qty[l.key]) > 0)
    .map((l) => ({ ...l, qty: Math.min(Number(qty[l.key]), l.pending) }));
  const serialLines = selected.filter((l) => l.serial);
  const sizedSerials = Object.fromEntries(serialLines.map((l) => {
    const n = Math.floor(l.qty); const cur = serials[l.key] || [];
    return [l.key, Array.from({ length: n }, (_, i) => cur[i] || "")];
  }));
  const shortLines = serialLines.filter((l) => sizedSerials[l.key].filter((v) => v.trim()).length < Math.floor(l.qty));

  const toggle = (k) => setChecked((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const idx = STEPS.findIndex((s) => s.key === step);
  const canNext = step === "items" ? selected.length > 0 : step === "transport" ? form.vehicle_no.trim() && form.driver_name.trim() : true;

  const submit = async () => {
    setBusy("Saving dispatch…");
    try {
      const payload = { ...form, lines: selected.map((s) => ({ key: s.key, qty: s.qty })),
        serials: Object.fromEntries(Object.entries(sizedSerials).map(([k, v]) => [k, v.filter((s) => s.trim())])) };
      const r = await api.post(`/field-items/${x.id}/dispatch`, payload);
      const ev = r.data.last_dispatch;
      for (let i = 0; i < photos.length; i++) {
        setBusy(`Photos upload: ${i + 1} / ${photos.length}`);
        const fd = new FormData();
        fd.append("file", photos[i].file); fd.append("kind", "dispatch"); fd.append("dispatch_id", ev.id);
        try { await api.post(`/field-items/${x.id}/photos`, fd); } catch (e) { toast.error(`Photo ${i + 1}: ${formatApiError(e)}`); }
      }
      toast.success(`Dispatch ${ev.dispatch_no} complete`);
      onDone(ev.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="dispatch-wizard">
      <Stepper step={step} />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/50">
        {step === "items" && <PickList lines={pending} checked={checked} toggle={toggle} qty={qty} setQty={setQty} />}
        {step === "serials" && <SerialStep lines={serialLines} serials={sizedSerials} setSerials={setSerials} />}
        {step === "transport" && <Transport form={form} setForm={setForm} photos={photos} setPhotos={setPhotos} />}
        {step === "verify" && <VerifyStep x={x} selected={selected} serials={sizedSerials} form={form} photos={photos} pending={pending} shortLines={shortLines} ack={ack} setAck={setAck} />}
      </div>
      <div className="border-t border-slate-200 bg-white px-4 sm:px-6 py-3 flex items-center gap-2">
        {idx > 0 ? (
          <Button type="button" variant="outline" className="h-11 rounded-full px-4" disabled={!!busy} data-testid="wizard-back-btn" onClick={() => setStep(STEPS[idx - 1].key)}><ArrowLeft size={16} className="mr-1" /> Back</Button>
        ) : <Button type="button" variant="ghost" className="h-11 rounded-full px-4" data-testid="wizard-cancel-btn" onClick={onCancel}>Cancel</Button>}
        <div className="flex-1 text-[11px] text-slate-500 hidden sm:block text-center">{selected.length} items · {photos.length} photos</div>
        {step !== "verify" ? (
          <Button data-testid="wizard-next-btn" disabled={!canNext} onClick={() => setStep(STEPS[idx + 1].key)}
            className="h-11 rounded-full px-6 font-bold bg-slate-900 hover:bg-slate-800 text-white ml-auto">
            Next {step === "serials" && shortLines.length > 0 && <Warning size={15} className="ml-1 text-brand-400" weight="fill" />}<CaretRight size={16} className="ml-1" />
          </Button>
        ) : (
          <Button data-testid="confirm-dispatch-btn" disabled={!!busy || (shortLines.length > 0 && !ack)} onClick={submit}
            className="h-11 rounded-full px-6 font-bold bg-emerald-600 hover:bg-emerald-700 text-white ml-auto">
            {busy ? <><CircleNotch size={16} className="mr-1.5 animate-spin" /> {busy}</> : <><PaperPlaneRight size={16} className="mr-1.5" /> Confirm Dispatch</>}
          </Button>
        )}
      </div>
    </div>
  );
}
